import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
} from '@headlessui/react';
import { Asset, Contract, Policy } from '@core/generated/gql-types';
import {
  fetchBlobUrlForPreview,
  isDocumentHeic,
  isImage,
} from '@/utils/preview.util';

// Note that the assetPreview and contractPreview props are optional.
// It is only possible to preview one of them at a time.
interface DocumentPreviewModalProps {
  assetPreview?: {
    previewAsset: Asset | null;
    setPreviewAsset: (previewAsset: Asset | null) => void;
    previewTitle: string | null;
    setPreviewTitle: (previewTitle: string | null) => void;
  };
  contractPreview?: {
    previewContract: Contract | null;
    setPreviewContract: (previewContract: Contract | null) => void;
  };
  policyPreview?: {
    previewPolicy: Policy | null;
    setPreviewPolicy: (previewPolicy: Policy | null) => void;
  };
}

const DocumentPreviewModal = ({
  assetPreview,
  contractPreview,
  policyPreview,
}: DocumentPreviewModalProps) => {
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { previewAsset, setPreviewAsset, previewTitle, setPreviewTitle } =
    assetPreview || {};
  const { previewContract, setPreviewContract } = contractPreview || {};
  const { previewPolicy, setPreviewPolicy } = policyPreview || {};

  // Fetch the blob URL for the preview asset if it exists.
  useEffect(() => {
    if (!previewAsset || !previewAsset.url) {
      setPreviewObjectUrl(null);
      setPreviewError(null);
      return;
    }
    setIsLoading(true);
    setPreviewError(null);
    let objectUrl: string | null = null;

    fetchBlobUrlForPreview(previewAsset)
      .then((url) => {
        objectUrl = url;
        setPreviewObjectUrl(url);
      })
      .catch((error) => {
        setPreviewError(error.message);
        toast.error(error.message, {
          position: 'bottom-left',
          autoClose: 3000,
        });
      })
      .finally(() => setIsLoading(false));

    // Cleanup function to revoke the preview object URL when
    // the component unmounts or the previewAsset changes.
    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      setPreviewObjectUrl(null);
      setPreviewError(null);
    };
  }, [previewAsset]);

  const closePreview = () => {
    if (previewObjectUrl) {
      window.URL.revokeObjectURL(previewObjectUrl);
      setPreviewObjectUrl(null);
      setPreviewTitle?.(null);
    }
    setPreviewAsset?.(null);
    setPreviewContract?.(null);
    setPreviewPolicy?.(null);
    setPreviewError(null);
  };

  if (!previewAsset && !previewContract && !previewPolicy) return null;

  return (
    <Transition
      show={
        previewAsset !== null ||
        previewContract !== null ||
        previewPolicy !== null
      }
      as={React.Fragment}
    >
      <Dialog as="div" className="relative z-50" onClose={closePreview}>
        {/* Background overlay */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        {/* Modal content */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="flex h-[80vh] w-[80vh] flex-col overflow-y-auto rounded-xl bg-white shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-medium text-gray-900"
                  >
                    {previewTitle ? `${previewTitle} ` : ''}
                    {previewContract && 'Contract '}
                    {previewPolicy && 'Policy '}
                    {'Preview'}
                  </DialogTitle>
                  <button
                    type="button"
                    onClick={closePreview}
                    className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="w-full flex-1 items-center justify-center px-4 py-3">
                  {isLoading && <div>Loading...</div>}
                  {previewError && <div>{previewError}</div>}

                  {/* Preview contract */}
                  {previewContract && previewContract.pdfUrl && (
                    <iframe
                      className="h-full w-full rounded-lg"
                      src={previewContract.pdfUrl}
                      title="Contract Preview"
                    />
                  )}

                  {/* Preview policy */}
                  {previewPolicy && previewPolicy.pdfUrl && (
                    <iframe
                      className="h-full min-h-[60vh] w-full rounded-lg"
                      src={previewPolicy.pdfUrl}
                      title="Policy Preview"
                    />
                  )}

                  {previewPolicy &&
                    !previewPolicy.pdfUrl &&
                    previewPolicy.policyHtmlContent && (
                      <div
                        className="prose prose-sm max-w-none overflow-auto"
                        // Tenant-authored policy HTML (same source as generated PDF).
                        dangerouslySetInnerHTML={{
                          __html: previewPolicy.policyHtmlContent,
                        }}
                      />
                    )}

                  {/* Preview asset */}
                  {previewAsset &&
                    previewObjectUrl &&
                    !previewError &&
                    (isImage(previewAsset?.key) ||
                    isDocumentHeic(previewAsset?.key) ? (
                      <img
                        className="rounded-lg"
                        src={previewObjectUrl}
                        alt={previewAsset.tag ?? 'Preview'}
                      />
                    ) : (
                      <iframe
                        className="h-full w-full rounded-lg"
                        src={previewObjectUrl}
                        title={previewAsset?.tag ?? 'Preview'}
                      />
                    ))}
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DocumentPreviewModal;
